import CalendarIcon from '@svgIcons/calendar.svg';
import Switch from '@components/Common/Switch';

import { isValueExist } from '@/utils/utils';
import {
    convertRelativeDate,
    getAdvancedDateFilter,
    getComparisonDateFilter,
    getDateFilter,
    isAdvancedDateFilter,
    renderPeriod,
} from '@/utils/dashboard';

import Arrow from '@svgIcons/descending.svg';
import InfoLineIcon from '@svgIcons/widget_info.svg';

import PanelOptionMixin from '../mixin';
import * as S from './styles';
import { IFRAME_EVENT_MESSAGE, WIDGET_TYPES } from '@/utils/consts';
import { BLUE, GRAY } from '@/styles/common.styled';
import { mapMutations } from 'vuex';
import withHMR from '@/utils/withHMR';

const { GOAL, SMALL_TABLE, CREATIVE } = WIDGET_TYPES;

const PanelDateFilter = {
    name: 'PanelDateFilter',
    mixins: [PanelOptionMixin],
    props: {
        disableComparison: {
            type: Boolean,
            default: false,
        },
        infoTooltipText: {
            type: String,
        },
        noTooltip: {
            type: Boolean,
            default: false,
        },
        uiDisabled: {
            type: Boolean,
            default: false,
        },
    },
    render() {
        const {
            widgetComparisonDateFilter,
            dateFilterProps,
            widgetType,
            uiDisabled,
        } = this;

        const widgetDateFilter = this.getWidgetDateFilter();
        const { disabled = true } = widgetDateFilter;

        const normalText = this.getNormalText();
        const comparisonText = this.getComparisonText();

        const disabledComparison =
            this.disableComparison ||
            R.includes(widgetType, [GOAL, SMALL_TABLE, CREATIVE]);

        return (
            <S.ComponentWrapper isDisabled={uiDisabled || disabled}>
                <div class="top-wrapper">
                    <div class="title">
                        <CalendarIcon v-view-box class="calendar-icon" />
                        <h1>{this.$t('NEW_DASHBOARD.period')}</h1>
                        {!this.noTooltip &&
                            this.wrapTooltip({
                                visible: true,
                                content: (
                                    <InfoLineIcon class="info-line-icon" />
                                ),
                                title:
                                    this.infoTooltipText ??
                                    this.$t(
                                        'WIDGET.widget_setting_date_filter_info'
                                    ),
                            })}
                    </div>

                    <Switch
                        value={!disabled}
                        type="big"
                        disabled={uiDisabled}
                        onClick={() => {
                            if (uiDisabled) return;

                            const isDisabled = !disabled;

                            const updatedWidgetDateFilter = isDisabled
                                ? {
                                      ...dateFilterProps.rawDateFilter,
                                      disabled: isDisabled,
                                  }
                                : {
                                      ...widgetDateFilter,
                                      disabled: isDisabled,
                                  };

                            const updatedWidgetComparisonDateFilter = isDisabled
                                ? {}
                                : widgetComparisonDateFilter;

                            this.$emit('update', {
                                widgetDateFilter: updatedWidgetDateFilter,
                                widgetComparisonDateFilter:
                                    updatedWidgetComparisonDateFilter,
                            });

                            if (isDisabled) {
                                this.postIframeMessage({
                                    type: IFRAME_EVENT_MESSAGE.TOGGLE_WIDGET_DATE_FILTER,
                                    origin: `VUE`,
                                    data: {
                                        open: false,
                                    },
                                });
                            }
                        }}
                    />
                </div>
                {!disabled && (
                    <div
                        class={{
                            'calender-wrapper': true,
                            'has-comparison': isValueExist(
                                widgetComparisonDateFilter
                            ),
                        }}
                        onClick={() => {
                            if (uiDisabled) return;
                            this.setShouldCloseAllSecondPanel(true);
                            this.postIframeMessage({
                                type: IFRAME_EVENT_MESSAGE.TOGGLE_WIDGET_DATE_FILTER,
                                origin: `VUE`,
                                data: {
                                    widgetDateFilter,
                                    widgetComparisonDateFilter,
                                    disabledComparison,
                                },
                            });
                            this.$nextTick(() => {
                                this.setShouldCloseAllSecondPanel(false);
                            });
                        }}
                    >
                        <div class="period-text-wrapper" slot="calendarChild">
                            {isValueExist(widgetComparisonDateFilter) ? (
                                <S.RangeWrapper>
                                    <S.DateWrapper>
                                        <S.Pointer color={BLUE(80)} />
                                        <div class="period-text">
                                            {normalText}
                                        </div>
                                    </S.DateWrapper>
                                    <S.DateWrapper>
                                        <S.Pointer color={GRAY(50)} />
                                        <div>{`${this.$t(
                                            'NEW_DASHBOARD.comparison_filter_date_prefix'
                                        )} ${comparisonText}`}</div>
                                    </S.DateWrapper>
                                </S.RangeWrapper>
                            ) : (
                                <div class="period-text">{normalText}</div>
                            )}
                        </div>
                        <div class="arrow-wrapper">
                            <Arrow v-view-box />
                        </div>
                    </div>
                )}
            </S.ComponentWrapper>
        );
    },
    methods: {
        ...mapMutations('newDashboard', ['setShouldCloseAllSecondPanel']),
        getWidgetDateFilter() {
            const { widgetDateFilter: _widgetDateFilter, dateFilterProps } =
                this;

            let widgetDateFilter = isValueExist(_widgetDateFilter)
                ? _widgetDateFilter
                : { ...dateFilterProps.rawDateFilter, disabled: true };

            const { from, to, disabled = true } = widgetDateFilter;

            // change to new date filter format
            if (from && to) {
                widgetDateFilter = {
                    disabled,
                    type: 'range',
                    values: {
                        range: {
                            from,
                            to,
                        },
                    },
                    todayExcluded: false,
                };
            }

            return widgetDateFilter;
        },
        getParsedDateFilter() {
            const widgetDateFilter = this.getWidgetDateFilter();
            if (isAdvancedDateFilter(widgetDateFilter)) {
                return {
                    parsedDateFilter: getAdvancedDateFilter({
                        dateFilter: widgetDateFilter,
                    }),
                };
            }

            const { type, values, todayExcluded = true } = widgetDateFilter;
            const dateFilterValue = R.pathOr('', [type, 'value'], values);

            /**
             * Date Filter
             */
            const date = R.equals(type, 'relative')
                ? convertRelativeDate(values[type])
                : values[type];

            const {
                dateFilter: parsedDateFilter,
                dateFilterStatus: parsedDateFilterStatus,
            } = getDateFilter({
                date,
                todayExcluded,
                dateFilterValue,
                savedValue: values[type],
            });

            return { parsedDateFilter, parsedDateFilterStatus };
        },
        getParsedComparisonDateFilter() {
            const { widgetComparisonDateFilter } = this;

            if (isAdvancedDateFilter(widgetComparisonDateFilter)) {
                return {
                    parsedComparisonDateFilter: getAdvancedDateFilter({
                        dateFilter: widgetComparisonDateFilter,
                    }),
                };
            }

            const { parsedDateFilter, parsedDateFilterStatus } =
                this.getParsedDateFilter();

            const savedComprisonDate = R.pathOr(
                {},
                ['values', 'range', 'period'],
                widgetComparisonDateFilter
            );

            const comparisonType = R.pathOr(
                'relative',
                ['type'],
                widgetComparisonDateFilter
            );
            const savedComprisonPeriod = R.pathOr(
                '',
                ['values', comparisonType, 'period'],
                widgetComparisonDateFilter
            );

            const { comparisonDateFilter: parsedComparisonDateFilter } =
                getComparisonDateFilter({
                    date:
                        comparisonType === 'relative' ? {} : savedComprisonDate,
                    comparedDate: parsedDateFilter,
                    period: savedComprisonPeriod,
                    dateFilterStatus: parsedDateFilterStatus,
                });

            return { parsedComparisonDateFilter };
        },
        getNormalText() {
            const widgetDateFilter = this.getWidgetDateFilter();
            const { type } = widgetDateFilter;
            const { parsedDateFilter, parsedDateFilterStatus } =
                this.getParsedDateFilter();

            switch (type) {
                case 'relative':
                    return this.$t(
                        `NEW_DASHBOARD.filter_date_${parsedDateFilterStatus}`
                    );
                case 'range':
                    return renderPeriod.bind(this)(parsedDateFilter);
                case 'advanced':
                    return renderPeriod.bind(this)(parsedDateFilter);
                default:
                    return '';
            }
        },
        getComparisonText() {
            const { parsedComparisonDateFilter } =
                this.getParsedComparisonDateFilter();

            return renderPeriod.bind(this)(parsedComparisonDateFilter);
        },
    },
};

export default withHMR(module, PanelDateFilter, PanelDateFilter.name);
